/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  isJobElementTotal,
  JobElementTotal,
  JobElementType,
  isJobEditable,
  jobCsCreateDialogRoute,
  jobSpecCreateDialogRoute,
  FilterNames,
  FilterRestService,
} from '@gh/builder-shared';
import { BuilderPermission, BuilderPermissionSet, JobStatus } from '@gh/builder-shared-data';
import {
  AppRouter, DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData, NumberTypes,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import {
  buildDateTimeColumn, buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { JobEstimatesJobListBusinessService } from './resources/job-estimates-job-list-business.service';
import {
  JobEstimatesListFilter,
  JobEstimatesListItem,
} from './resources/job-estimates-job-list.model';
import {
  conditionalRowPostCreateDecorator, GridSettingsService,
  rendererIconByName,
  textOverlayRowPostCreate,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { DrawListItem } from '../../builder-accounting/resources/draw-list.model';
import { filter$ } from '@gh/rx/operators';
import { TranslationService } from '@gh/core-mls';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  standalone: false,
  selector: 'gh-job-estimates-job-list',
  templateUrl: 'job-estimates-job-list.component.html',
  styleUrls: ['job-estimates-job-list.component.scss'],
 })
export class JobEstimatesJobListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<JobEstimatesListItem>;
  form: FilterableFormState<JobEstimatesListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  total$: Observable<any>;
  _total  = new BehaviorSubject<JobElementTotal[]>([{type: JobElementType.Total, id: 'total-count'}]);
  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: UserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: JobEstimatesJobListBusinessService,
              private notifications: NotificationService,
              private router: AppRouter,
              private filterService: FilterRestService,
              private translations: TranslationService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private gridService: GridSettingsService,
              public gridDefinitionFactory: GridDefinitionFactory) {
    this.total$ = this._total.asObservable();
    this.gridService.setCurrentTable('jobEstimatesList');
  }

  isJobDpp(job: JobEstimatesListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout} = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        isEstimateAuthorized: {
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
        contractAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        variationsAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        totalAmount: buildNumberColumnLeft(NumberTypes.Price),
        totalAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        estimateCost: buildNumberColumnLeft(NumberTypes.Price),
        estimatedProfit: buildNumberColumnLeft(NumberTypes.Price),
        profitMargin: buildNumberColumnLeft(NumberTypes.Price),
        originalBudget: buildNumberColumnLeft(NumberTypes.Price),
        originalContractAmount: buildNumberColumnLeft(NumberTypes.Price),
        originalProfitMargin: buildNumberColumnLeft(NumberTypes.Price),
        currentBudget: buildNumberColumnLeft(NumberTypes.Price),

        status: {
          cellClass: ({data}: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
            [JobStatus.Finalized]: 'finalized-status',
          }[data.statusValue]),
        },
        contractDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        councilApprovedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        csDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        authToCommenceDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        finalizedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },

      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id),
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isJobElementTotal(data),
          textOverlayRowPostCreate(this.translations.get('lbl_total').toUpperCase())),
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showFinalized: void 0,
        showDpp: void 0,
        showActive: void 0,
        showNotProceeding: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.dataSource = this.businessService.findAllAsDataSource({
        ...filter,
      });
      if (!isInit) {
        this.filterService.update(FilterNames.jobEstimatesList, filter).subscribe();
      }
      isInit = false;
    });
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  onJobDetailsOpen(job: JobEstimatesListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobDetails.ANY)) {
      if (job.isDpp) {
        this.router.navigateToChild(['/job-admin/jobs/dpp', job.id], {
          parentTitle: 'lbl_job_estimates',
        });
      } else {
        this.router.navigateToChild(['/job-admin/jobs', job.id], {
          parentTitle: 'lbl_job_estimates',
        });
      }
    }
  }

  goToAel(job?: JobEstimatesListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_job_estimates',
      entityType: 'JOB',
      entityId: job ? job.id : undefined,
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onJobEstimateOpen(job: JobEstimatesListItem): void {
    if (!this.isElement(job)) {
      return;
    }
    this.router.navigateToChild(['job-admin/jobs', job.id, 'estimate'], {
      parentTitle: 'lbl_job_estimates',
    });
  }

  onFinancialsOpen(job: JobEstimatesListItem): void {
    this.router.navigateToChild(['job-admin', 'job-financials', job.id], {
      parentTitle: 'lbl_job_estimates',
    });
  }

  onJobScheduleOpen(): void {
    // not implemented yet
  }

  onJobOrderOpen(job: JobEstimatesListItem): void {
    this.router.navigateToChild(['/job-admin/job-orders', job.id], {
      parentTitle: 'lbl_job_estimates',
    });
  }

  onJobChangeOrderOpen(job: JobEstimatesListItem): void {
    this.router.navigateToChild([`/job-admin/job-change-orders`, job.id], {
      parentTitle: 'lbl_job_estimates',
    });
  }

  onColorSelectionOpen(job: DrawListItem): void {
    // this.jobNavigation.onColorSelectionOpen(job, 'lbl_draws');
    if (job.isCsExist) {
      this.router.navigateToChild(['job-admin/jobs', job.id, 'color-selection'], { parentTitle: 'lbl_job_estimates' });
    } else if (!isJobEditable(job)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT)) {
      this.notifications.error('msg_cs_empty');
    } else {
      this.dialogService.openWithInput(jobCsCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((csm: any) =>
                     this.router.navigateToChild(
                       ['job-admin/jobs', job.id, 'color-selection', 'new'],
                       {
                         queryParams: { csMasterId: csm.master.id },
                         parentTitle: 'lbl_job_estimates',
                       }));
    }
  }

  onJobSpecificationOpen(job: DrawListItem): void {
    if (job.isSpecExist) {
      this.router.navigateToChild([`/job-admin/jobs/${job.id}/spec`], {parentTitle: 'lbl_job_estimates'});
    } else if (!isJobEditable(job)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)) {
      this.notifications.error('msg_spec_empty');
    } else {
      this.dialogService.openWithInput(jobSpecCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((formValue: any) => {
          this.router.navigateToChild(
            [`/job-admin/jobs/${job.id}/spec/new`],
            {
              queryParams: {specTemplateId: formValue.jobSpecTemplate.id},
              parentTitle: 'lbl_job_estimates',
            });
        });
    }
  }

  onLoadData(event: any): void {
    const totals = event.totals;
    const total = {};
    for (const key in totals) {
      if (Object.prototype.hasOwnProperty.call(totals, key)) {
        total[key] = totals[key];
      }
    }
    this._total.next([{type: JobElementType.Total, id: 'total-count', variationId: 'variation:footer', ...total  }]);
  }

  isElement(element: any): boolean {
    return element.type !== JobElementType.Total;
  }
}
